import React, { useEffect, useState } from 'react';
import api from '../services/api';

const now = new Date();
const currentYear = now.getFullYear();
const currentMonth = now.getMonth() + 1;
const monthOptions = Array.from({ length: 12 }, (_, idx) => idx + 1);

const formatNumber = (value) => {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return '-';
  return num.toLocaleString('ko-KR');
};

const tableContainer = {
  marginTop: '20px',
  border: '1px solid #e0e0e0',
  borderRadius: '8px',
  overflow: 'auto',
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.85rem',
};

const thStyle = {
  background: '#f2f4f6',
  borderBottom: '1px solid #d0d7de',
  padding: '10px',
  textAlign: 'center',
  fontWeight: 600,
  whiteSpace: 'nowrap',
};

const tdStyle = {
  borderBottom: '1px solid #eef2f6',
  padding: '10px',
  textAlign: 'center',
  whiteSpace: 'nowrap',
};

const filterGroup = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flexWrap: 'wrap',
  marginBottom: '16px',
};

const labelStyle = { fontWeight: 600 };
const inputStyle = {
  padding: '8px 10px',
  borderRadius: '6px',
  border: '1px solid #d0d7de',
};

const buttonStyle = {
  padding: '10px 16px',
  background: '#2563eb',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
};

const secondaryButton = {
  ...buttonStyle,
  background: '#475569',
};

const infoBox = {
  marginTop: '16px',
  padding: '12px 16px',
  background: '#f8fafc',
  border: '1px solid #cbd5f5',
  borderRadius: '8px',
  fontSize: '0.9rem',
  lineHeight: 1.5,
};

const columns = [
  ['성명', (item) => item.Participant?.name],
  ['사업단', (item) => item.Project?.projectName || '-'],
  ['기본급', (item) => formatNumber(item.basePay)],
  ['주휴수당', (item) => formatNumber(item.weeklyAllowance)],
  ['시간외수당', (item) => formatNumber(item.overtimeAllowance)],
  ['연차수당', (item) => formatNumber(item.annualAllowance)],
  ['기타수당', (item) => formatNumber(item.otherAllowance)],
  ['건강보험', (item) => formatNumber(item.healthInsurance)],
  ['요양보험', (item) => formatNumber(item.longTermCareInsurance)],
  ['고용보험', (item) => formatNumber(item.employmentInsurance)],
  ['산재보험', (item) => formatNumber(item.industrialAccidentInsurance)],
  ['소득세', (item) => formatNumber(item.incomeTax)],
  ['지방세', (item) => formatNumber(item.localTax)],
  ['차감지급액', (item) => formatNumber(item.netPay)],
];

function filenameFromDisposition(header, fallback) {
  if (!header) return fallback;
  const encoded = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch (error) {
      return fallback;
    }
  }
  const plain = header.match(/filename="?([^";]+)"?/i);
  return plain ? plain[1] : fallback;
}

function PayrollAutoTransferPage() {
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(currentMonth);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [nameKeyword, setNameKeyword] = useState('');
  const [payrolls, setPayrolls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [message, setMessage] = useState(null);

  const fetchProjects = async (targetYear = year) => {
    try {
      const response = await api.get('/projects', { params: { year: targetYear } });
      setProjects(response.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchPayrolls = async (overrides = {}) => {
    const effectiveProjectId = overrides.projectId ?? projectId;
    setLoading(true);
    setMessage(null);
    try {
      const response = await api.get('/payroll', {
        params: {
          year,
          month,
          includeZero: true,
          projectId: effectiveProjectId || undefined,
          name: nameKeyword || undefined,
        },
      });
      setPayrolls(response.data || []);
    } catch (error) {
      console.error(error);
      setPayrolls([]);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || '급여 목록을 불러오지 못했습니다.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects(year);
  }, [year]);

  const handleSearch = () => {
    fetchPayrolls();
  };

  const handleExcel = async () => {
    if (!projectId) {
      setMessage({ type: 'error', text: '엑셀 출력 전에 사업단을 선택해 주세요.' });
      return;
    }
    setExporting(true);
    setMessage(null);
    try {
      const res = await api.get('/payroll/auto-transfer/export', {
        params: {
          year,
          month,
          projectId,
          ...(nameKeyword.trim() ? { name: nameKeyword.trim() } : {}),
        },
        responseType: 'blob',
      });
      const projectName = projects.find((project) => String(project.id) === String(projectId))?.projectName || '사업단';
      const safeProjectName = String(projectName).replace(/[\\/:*?"<>|]/g, '').trim() || '사업단';
      const fallback = `${safeProjectName}_${year}년_${month}월.xlsx`;
      const filename = filenameFromDisposition(res.headers['content-disposition'], fallback);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      let warningText = '';
      const warningHeader = res.headers['x-transfer-warnings'];
      if (warningHeader) {
        try {
          const list = JSON.parse(decodeURIComponent(warningHeader));
          if (Array.isArray(list) && list.length) {
            warningText = `은행코드를 찾지 못한 인원: ${list
              .map((item) => `${item.name}(${item.bankName || '은행명 없음'})`)
              .join(', ')}. 엑셀의 은행코드 칸이 비어 있습니다.`;
          }
        } catch (error) {
          warningText = '일부 은행명을 코드로 바꾸지 못했습니다. 엑셀의 은행코드 칸을 확인해 주세요.';
        }
      }
      setMessage({
        type: warningText ? 'warning' : 'success',
        text: warningText || '급여 자동이체 엑셀을 내려받았습니다.',
      });
    } catch (error) {
      let text = '엑셀 다운로드에 실패했습니다.';
      if (error.response?.data instanceof Blob) {
        try {
          const raw = await error.response.data.text();
          const parsed = JSON.parse(raw);
          if (parsed.message) text = parsed.message;
        } catch (parseError) {
          /* 본문이 JSON이 아니면 기본 문구를 유지한다. */
        }
      } else if (error.response?.data?.message) {
        text = error.response.data.message;
      }
      setMessage({ type: 'error', text });
    } finally {
      setExporting(false);
    }
  };

  const totalNet = payrolls.reduce((sum, item) => sum + Number(item.netPay || 0), 0);

  return (
    <div>
      <h2 style={{ marginBottom: '6px' }}>자동이체 자료 출력</h2>
      <div style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '10px' }}>
        참여자 급여 관리와 같은 조건으로 조회한 뒤, 사업단별 자동이체 엑셀을 만듭니다.
        현금 지급자와 계좌가 없는 사람은 엑셀에서 빠집니다.
      </div>

      <div style={filterGroup}>
        <label style={labelStyle}>
          연도
          <input
            type="number"
            value={year}
            min="2000"
            style={{ ...inputStyle, marginLeft: '6px', width: '110px' }}
            onChange={(e) => setYear(Number(e.target.value))}
          />
        </label>
        <label style={labelStyle}>
          월
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            style={{ ...inputStyle, marginLeft: '6px' }}
          >
            {monthOptions.map((m) => (
              <option key={m} value={m}>
                {m}월
              </option>
            ))}
          </select>
        </label>
        <label style={labelStyle}>
          사업단
          <select
            value={projectId}
            onChange={(e) => {
              const nextProjectId = e.target.value;
              setProjectId(nextProjectId);
              fetchPayrolls({ projectId: nextProjectId });
            }}
            style={{ ...inputStyle, marginLeft: '6px' }}
          >
            <option value="">전체</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.projectName}
              </option>
            ))}
          </select>
        </label>
        <label style={labelStyle}>
          성명
          <input
            type="text"
            value={nameKeyword}
            style={{ ...inputStyle, marginLeft: '6px' }}
            placeholder="성명 검색"
            onChange={(e) => setNameKeyword(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') handleSearch();
            }}
          />
        </label>
        <button type="button" style={secondaryButton} onClick={handleSearch} disabled={loading}>
          조회
        </button>
        <button
          type="button"
          style={{ ...buttonStyle, opacity: !projectId || exporting ? 0.6 : 1 }}
          onClick={handleExcel}
          disabled={!projectId || exporting || loading}
        >
          {exporting ? '엑셀 생성 중...' : '엑셀 출력'}
        </button>
      </div>

      {!projectId && (
        <div style={{ ...infoBox, marginTop: 0 }}>
          엑셀은 출금계좌가 사업마다 달라서, 사업단을 선택한 뒤에만 출력할 수 있습니다.
        </div>
      )}

      {message && (
        <div
          style={{
            ...infoBox,
            borderColor: message.type === 'error' ? '#fca5a5' : message.type === 'warning' ? '#f5d78e' : '#93c5fd',
            background: message.type === 'error' ? '#fef2f2' : message.type === 'warning' ? '#fff8e6' : '#eff6ff',
            color: message.type === 'error' ? '#b91c1c' : message.type === 'warning' ? '#92400e' : '#1d4ed8',
          }}
        >
          {message.text}
        </div>
      )}

      <div style={infoBox}>
        <div style={{ marginBottom: '8px', fontWeight: 600 }}>조회 인원: {payrolls.length}명</div>
        <div>총 차감지급액: {formatNumber(totalNet)}원</div>
        <div style={{ marginTop: '8px', color: '#64748b' }}>
          인건비는 기본급에서 개인공제(건강보험·요양보험·고용보험·산재보험·소득세·지방세)를 뺀 금액입니다.
          시간외수당·연차수당·기타수당·개인공제는 해당 금액이 있는 경우에만 시트가 만들어집니다.
        </div>
      </div>

      <div style={tableContainer}>
        <table style={tableStyle}>
          <thead>
            <tr>
              {columns.map(([label]) => (
                <th key={label} style={thStyle}>{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td style={tdStyle} colSpan={columns.length}>불러오는 중...</td>
              </tr>
            ) : payrolls.length === 0 ? (
              <tr>
                <td style={tdStyle} colSpan={columns.length}>데이터가 없습니다.</td>
              </tr>
            ) : (
              payrolls.map((item) => (
                <tr key={item.id}>
                  {columns.map(([label, render]) => (
                    <td key={label} style={tdStyle}>{render(item)}</td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default PayrollAutoTransferPage;
